/**
 * Composition Engine — the orchestrator.
 *
 * Takes a DesignSpec (the decisions OpenAI will make later; today the admin
 * playground makes them) and coordinates every other engine to produce an
 * ordered CommandBuffer. This is the only module that knows about all the
 * others; each sub-engine stays independently usable.
 *
 * DesignSpec = {
 *   canvas: { width, height },
 *   layout: 'classic-flyer',
 *   palette: { seed: '#7c3aed', harmony: 'complementary', dark: false },
 *   typography: { pairingId: 'modern-sans', baseSize: 16, scale: 'balanced' },
 *   spacing: 'comfortable',
 *   decoration: 'organic',
 *   content: { primaryHeading: { text }, secondaryHeading: { text },
 *              body: { text }, offer: { text }, cta: { text }, footer: { text } },
 *   components: [{ id, zone, props }],   // extra components beyond the grammar
 * }
 */

import { CommandBuffer, resetCommandIds } from './commandEngine';
import { resolveLayout } from './layoutEngine';
import { expandGrammar, getGrammarNode } from './designGrammarEngine';
import { resolveTypography, NODE_TYPE_ROLE } from './typographyEngine';
import { generatePalette, readableTextOn } from './colorHarmonyEngine';
import { resolveSpacing, padRect, distributeStack } from './spacingEngine';
import { alignInZone, snapToGrid } from './alignmentEngine';
import { buildComponent } from './componentEngine';
import { planDecorations } from './decorationEngine';

/** Relative heights of grammar nodes when stacked inside their zone. */
const NODE_WEIGHTS = {
  primaryHeading: 3,
  secondaryHeading: 1.5,
  body: 2,
  offer: 2,
  cta: 1.5,
};

const DEFAULT_SPEC = {
  canvas: { width: 1080, height: 1350 },
  layout: 'classic-flyer',
  palette: { seed: '#7c3aed', harmony: 'complementary', dark: false },
  typography: { pairingId: 'modern-sans', baseSize: 16, scale: 'balanced' },
  spacing: 'comfortable',
  decoration: 'minimal',
  content: {},
  components: [],
};

export const normalizeSpec = (spec = {}) => ({
  ...DEFAULT_SPEC,
  ...spec,
  canvas: { ...DEFAULT_SPEC.canvas, ...spec.canvas },
  palette: { ...DEFAULT_SPEC.palette, ...spec.palette },
  typography: { ...DEFAULT_SPEC.typography, ...spec.typography },
  content: { ...spec.content },
  components: spec.components || [],
});

/**
 * Compose a template.
 * Returns { buffer, commands, layout, tokens, nodes } — the validation and
 * quality engines consume this whole result.
 */
export const compose = (rawSpec) => {
  const spec = normalizeSpec(rawSpec);
  resetCommandIds();

  const layout = resolveLayout(spec.layout, spec.canvas);
  const palette = generatePalette(spec.palette);
  const typography = resolveTypography(spec.typography);
  const spacing = resolveSpacing(spec.spacing);
  const tokens = { palette: palette.roles, typography: typography.roles, spacing };

  const buffer = new CommandBuffer();
  const placed = []; // { nodeType, rect, commandId } for validation/quality

  // 1. Background (grammar root)
  buffer.push('createCanvas', {
    width: spec.canvas.width,
    height: spec.canvas.height,
    background: palette.roles.background,
  });

  // 2. Decorations first — they sit behind content in z-order.
  const decorations = planDecorations(spec.decoration, layout);
  decorations.forEach(({ slot, componentId, props, rect }) => {
    buildComponent(componentId, rect, tokens, props).forEach(({ command, params }) => {
      buffer.push(command, params, `decoration:${slot}`);
    });
  });

  // 3. Grammar nodes, zone by zone, stacked with spacing rules.
  const nodes = expandGrammar(spec.content);
  const zoneNodes = {};
  nodes.forEach((node) => {
    if (!node.zone) return;
    (zoneNodes[node.zone] = zoneNodes[node.zone] || []).push(node);
  });

  Object.entries(zoneNodes).forEach(([zoneRole, group]) => {
    const zone = layout.zones[zoneRole];
    if (!zone) return;

    // Structural nodes (hero/footer) mark the section itself.
    const structural = group.filter((n) => ['hero', 'footer'].includes(n.type));
    structural.forEach((node) => {
      const cmd = buffer.push(node.command, { ...zone }, `zone:${zoneRole}`);
      placed.push({ nodeType: node.type, rect: { ...zone }, commandId: cmd.id, structural: true });
    });

    const contentNodes = group.filter((n) => !structural.includes(n));
    if (!contentNodes.length) return;

    const inner = padRect(zone, spacing.zonePadding);
    const slots = distributeStack(
      inner,
      contentNodes.map((n) => ({ weight: NODE_WEIGHTS[n.type] || 1 })),
      spacing.stackGap
    );

    contentNodes.forEach((node, i) => {
      const role = NODE_TYPE_ROLE[node.type] || 'body';
      const type = tokens.typography[role];
      const slot = slots[i];

      // Text boxes take the slot width; CTAs/badges hug their content.
      const isButton = node.command === 'createCTA' || node.command === 'createBadge';
      const text = node.payload.text || '';
      const box = isButton
        ? {
            width: Math.min(inner.width, Math.max(180, text.length * type.fontSize * 0.62 + spacing.scale.xl)),
            height: Math.min(slot.height, type.fontSize + spacing.scale.lg),
          }
        : { width: slot.width, height: slot.height };

      const rect = snapToGrid(
        alignInZone(box, slot, { horizontal: zone.align, vertical: 'middle' }),
        4
      );

      const params = { ...rect, text, ...type, align: zone.align };
      if (node.command === 'createCTA') {
        params.background = tokens.palette.primary;
        params.color = readableTextOn(tokens.palette.primary);
        params.borderRadius = Math.round(rect.height / 2);
      } else if (node.command === 'createBadge') {
        params.background = tokens.palette.accent;
        params.color = readableTextOn(tokens.palette.accent);
        params.borderRadius = spacing.scale.sm;
      } else {
        params.color = tokens.palette.text;
      }

      const cmd = buffer.push(node.command, params, `zone:${zoneRole}`);
      placed.push({ nodeType: node.type, rect, commandId: cmd.id, text: !!text });
    });
  });

  // 4. Extra components requested by the spec.
  spec.components.forEach(({ id, zone: zoneRole, props }) => {
    const zone = layout.zones[zoneRole] || layout.safeArea;
    const inner = padRect(zone, spacing.zonePadding);
    buildComponent(id, inner, tokens, props || {}).forEach(({ command, params }) => {
      const cmd = buffer.push(command, params, `component:${id}`);
      placed.push({ nodeType: `component:${id}`, rect: {
        x: params.x, y: params.y, width: params.width, height: params.height,
      }, commandId: cmd.id });
    });
  });

  // 5. Finalize.
  buffer.push('exportTemplate', { format: 'template_json' });

  return {
    spec,
    buffer,
    commands: buffer.toJSON(),
    layout,
    tokens: { ...tokens, paletteMeta: palette, typographyMeta: typography },
    nodes,
    placed,
    decorations,
  };
};
